/**
 * The ONE frontend reader of the platform agent-noun preset.
 *
 * SPEC-FRONTEND-RULE-096 (D-368): `system_settings.agent_noun_preset` is the
 * platform-wide word the TENANT SPA uses for an agent - `agents` (today's
 * wording, the default), `assistants`, `chatbots`, `chat_robots` or
 * `chat_surfaces`. The owner's reason, 2026-09-18: the municipal tenants are
 * staffed by clerks to whom "Agent" means nothing, so the operator picks the
 * word they understand.
 *
 * The SPAs read it from the public `GET /api/v1/features`
 * (SPEC-GO-API-CONTRACT-035); no other module fetches that resource
 * (SPEC-FRONTEND-RULE-077: one key, one fetcher, one owner) - this module
 * IMPORTS `platformFeaturesQueryOptions()` rather than declaring a second
 * queryFn on `['platform','features']`.
 *
 * SCOPE: the tenant SPA only. The platform admin SPA and the workspace SPA keep
 * the fixed wording - they are cross-tenant surfaces where one tenant-facing
 * word is meaningless.
 *
 * WHY SIX FORMS AND NOT TWO. Hungarian selects its definite article from the
 * sound that follows it: `az ugynok` and `az asszisztens`, but `a chatbot`.
 * 43 of the 82 strings this feature governs carry an article, so a resolver
 * exposing only a singular and a plural would produce `az chatbot` in more than
 * half of them - wrong Hungarian that no gate detects, because the i18n checker
 * looks for missing entries, not grammar. Each preset therefore supplies the
 * article-bearing forms as data; a render site never concatenates an article.
 *
 * English needs no article variation grammatically, so its article-bearing
 * forms are simply "the agent" / "The agent". One render site serves both
 * locales because each locale's message uses the forms its own grammar needs.
 *
 * AMENDED 2026-09-20 (D-371, SPEC-FRONTEND-RULE-096 as amended): a sixth
 * preset, `custom`, renders words the operator TYPED rather than words this
 * module catalogues. All eight of them are typed - singular, plural, definite
 * singular and definite plural, in Hungarian AND in English - and NOTHING is
 * derived from anything else, because the Hungarian plural follows vowel
 * harmony (ugynok/ugynokok, asszisztens/asszisztensek, chatbot/chatbotok) and
 * the definite article is picked by the FOLLOWING SOUND rather than the
 * spelling. A deriver would be wrong at a rate nobody measures, and a wrong
 * word in a language the reader does not proofread degrades in silence - which
 * is exactly the defect this amendment follows.
 *
 * @module utils/agentNoun
 * @see TASK-FE-AGENT-NOUN-RESOLVER
 * @see TASK-FE-AGENT-NOUN-CUSTOM
 */
import { msg, t } from "@lingui/core/macro";
import type { I18n, MessageDescriptor } from "@lingui/core";
import { useLingui } from "@lingui/react";
import { useQuery } from "@tanstack/react-query";
import { createContext, createElement, useContext, useMemo, type ReactElement, type ReactNode } from "react";

import { platformFeaturesQueryOptions } from "./loginIdentifierMode";
import { isBlankValue } from "./visibleText";

/** The six presets. Mirrors the Go enumeration in internal/repository/settings.go. */
export type AgentNounPreset =
  | "agents"
  | "assistants"
  | "chatbots"
  | "chat_robots"
  | "chat_surfaces"
  | "custom";

/**
 * The five presets whose words live in the CATALOG. `custom` is deliberately
 * excluded: its words are typed by the operator and arrive in the payload, so
 * it has no `msg` descriptors and must never be looked up in the table below.
 */
export type AgentNounCatalogPreset = Exclude<AgentNounPreset, "custom">;

/**
 * Mirrors DefaultSystemSettings().AgentNounPreset (Go); the loading and error
 * value. It is today's wording deliberately: a failed fetch must degrade to the
 * current product, never to an empty heading or a half-renamed screen.
 */
export const DEFAULT_AGENT_NOUN_PRESET: AgentNounCatalogPreset = "agents";

/** Every preset, in the order the platform admin form offers them. */
export const AGENT_NOUN_PRESETS: readonly AgentNounPreset[] = [
  "agents",
  "assistants",
  "chatbots",
  "chat_robots",
  "chat_surfaces",
  "custom",
] as const;

/**
 * The five presets the CATALOG carries words for, in the same order. It is the
 * list to iterate when reaching into NOUN_DESCRIPTORS; AGENT_NOUN_PRESETS is
 * the list the admin picker offers, and the two differ by exactly `custom`,
 * whose words are typed rather than catalogued (D-371).
 */
export const AGENT_NOUN_CATALOG_PRESETS: readonly AgentNounCatalogPreset[] = [
  "agents",
  "assistants",
  "chatbots",
  "chat_robots",
  "chat_surfaces",
] as const;

/**
 * The surface forms one preset supplies. `noun`/`nounPlural` are the bare
 * words; the `*Def` forms carry the definite article, in sentence-initial
 * (`Noun...`) and mid-sentence (`noun...`) casing, because a render site cannot
 * capitalize an interpolated value.
 */
export interface AgentNounForms {
  /** Sentence-initial or standalone label: "Agent" / "Asszisztens". */
  Noun: string;
  /** Mid-sentence: "agent" / "asszisztens". Derived, see lowerFirst(). */
  noun: string;
  /** Sentence-initial or standalone label, plural. */
  NounPlural: string;
  /** Mid-sentence, plural. Derived. */
  nounPlural: string;
  /** Mid-sentence with the definite article: "the agent" / "az asszisztens". */
  nounDef: string;
  /** Sentence-initial with the definite article: "The agent" / "Az asszisztens". */
  NounDef: string;
  nounPluralDef: string;
  NounPluralDef: string;
}

/**
 * The four forms of the noun IN ONE LANGUAGE, each typed by the operator.
 * Mirrors repository.AgentNounWords (Go) field for field, including the JSON
 * spelling: the payload is consumed as it arrives, never re-keyed, so a rename
 * on either side is a type error rather than a word that quietly stops
 * rendering.
 */
export interface AgentNounTypedWords {
  /** Bare singular, standalone or sentence-initial: "Asszisztens". */
  singular: string;
  /** Bare plural: "Asszisztensek". */
  plural: string;
  /** With the definite article, mid-sentence: "az asszisztens". */
  definite_singular: string;
  /** With the definite article, plural, mid-sentence: "az asszisztensek". */
  definite_plural: string;
}

/** The whole typed vocabulary: four Hungarian forms and four English ones. */
export interface AgentNounCustomWords {
  hu: AgentNounTypedWords;
  en: AgentNounTypedWords;
}

/** The eight field paths, in the order the admin card shows them. */
export const AGENT_NOUN_CUSTOM_FIELDS: readonly {
  locale: keyof AgentNounCustomWords;
  form: keyof AgentNounTypedWords;
}[] = [
  { locale: "hu", form: "singular" },
  { locale: "hu", form: "plural" },
  { locale: "hu", form: "definite_singular" },
  { locale: "hu", form: "definite_plural" },
  { locale: "en", form: "singular" },
  { locale: "en", form: "plural" },
  { locale: "en", form: "definite_singular" },
  { locale: "en", form: "definite_plural" },
] as const;

/** An all-empty vocabulary - the admin form's initial draft. */
export function emptyAgentNounCustomWords(): AgentNounCustomWords {
  const blank = (): AgentNounTypedWords => ({
    singular: "",
    plural: "",
    definite_singular: "",
    definite_plural: "",
  });
  return { hu: blank(), en: blank() };
}

function typedWordsFrom(value: unknown): AgentNounTypedWords {
  const src = (value ?? {}) as Partial<Record<keyof AgentNounTypedWords, unknown>>;
  const str = (v: unknown): string => (typeof v === "string" ? v : "");
  return {
    singular: str(src.singular),
    plural: str(src.plural),
    definite_singular: str(src.definite_singular),
    definite_plural: str(src.definite_plural),
  };
}

/**
 * Narrow an arbitrary payload value into the typed vocabulary. Every missing
 * or non-string field becomes the empty string, which
 * `isAgentNounCustomComplete` then reports as INCOMPLETE - so a malformed
 * payload degrades to the catalogued wording rather than rendering a blank
 * word, on the same terms as `toAgentNounPreset` above.
 */
export function toAgentNounCustomWords(value: unknown): AgentNounCustomWords {
  const src = (value ?? {}) as Partial<Record<keyof AgentNounCustomWords, unknown>>;
  return { hu: typedWordsFrom(src.hu), en: typedWordsFrom(src.en) };
}

/**
 * All eight forms present and non-blank. Mirrors the Go
 * AgentNounCustomComplete, which TASK-VTI-DELEGATIONS moves onto the shared
 * table in the same wave, so both sides keep answering alike.
 *
 * A form is incomplete when it is "" - which for this field means ABSENT
 * (SPEC-FRONTEND-RULE-096 (11)) - or when `isBlankValue` says it shows the
 * reader nothing (D-378, SPEC-SERVICE-INTEGRATION RULE-INTEG-031 (a), via the
 * one client blank test in web/src/utils/visibleText.ts). The `.trim() !== ""`
 * test this replaced saw only the blanks the browser's own `\s` knows, so a
 * noun made of zero-width characters, of U+3164 or of U+2800 counted as a real
 * word and rendered as a gap in the sentence. The two tests are asked in that
 * order on purpose: the helper reports "" as NOT blank (RULE-INTEG-031 (b)),
 * and it is this caller, never the helper, that says what empty means here.
 *
 * It is the render-side guard that makes the feature migration-free: a stored
 * `custom` whose words are incomplete - an old row, a hand-edited store -
 * falls back to the `agents` catalog exactly as an unknown preset does. The
 * GATEWAY already refuses to save such a state (a 400 naming the field); this
 * is the second layer, not the first, and neither is a silent fallback for
 * something an operator typed.
 */
export function isAgentNounCustomComplete(words: AgentNounCustomWords | null): boolean {
  if (!words) return false;
  return AGENT_NOUN_CUSTOM_FIELDS.every(({ locale, form }) => {
    const value = words[locale][form];
    return value !== "" && !isBlankValue(value);
  });
}

/**
 * The compile-time descriptor table. Every entry is a literal `msg` template so
 * `lingui extract` can see it: a message id assembled at runtime is PROHIBITED
 * by RULE-093, because `tools/check-i18n-freshness.py` only scans literal
 * templates and an assembled id would render as the raw id string in both
 * locales with every gate green.
 */
type CatalogForm = "Noun" | "NounPlural" | "nounDef" | "NounDef" | "nounPluralDef" | "NounPluralDef";

const NOUN_DESCRIPTORS: Record<
  AgentNounCatalogPreset,
  Record<CatalogForm, MessageDescriptor>
> = {
  // The default preset REUSES the catalog's existing "Agent" / "Agents"
  // entries, which is the point: it must render exactly today's wording, and
  // those are the entries that produce it. They are shared with the platform
  // admin and workspace SPAs - read here, never rewritten, so nothing leaks.
  agents: {
    Noun: msg`Agent`,
    NounPlural: msg`Agents`,
    nounDef: msg`the agent`,
    NounDef: msg`The agent`,
    nounPluralDef: msg`the agents`,
    NounPluralDef: msg`The agents`,
  },
  assistants: {
    Noun: msg`Assistant`,
    NounPlural: msg`Assistants`,
    nounDef: msg`the assistant`,
    NounDef: msg`The assistant`,
    nounPluralDef: msg`the assistants`,
    NounPluralDef: msg`The assistants`,
  },
  chatbots: {
    Noun: msg`Chatbot`,
    NounPlural: msg`Chatbots`,
    nounDef: msg`the chatbot`,
    NounDef: msg`The chatbot`,
    nounPluralDef: msg`the chatbots`,
    NounPluralDef: msg`The chatbots`,
  },
  chat_robots: {
    Noun: msg`Chat robot`,
    NounPlural: msg`Chat robots`,
    nounDef: msg`the chat robot`,
    NounDef: msg`The chat robot`,
    nounPluralDef: msg`the chat robots`,
    NounPluralDef: msg`The chat robots`,
  },
  chat_surfaces: {
    Noun: msg`Chat surface`,
    NounPlural: msg`Chat surfaces`,
    nounDef: msg`the chat surface`,
    NounDef: msg`The chat surface`,
    nounPluralDef: msg`the chat surfaces`,
    NounPluralDef: msg`The chat surfaces`,
  },
};

/** The form keys, exported so the inventory gate can assert completeness. */
export const AGENT_NOUN_FORM_KEYS: readonly CatalogForm[] = [
  "Noun",
  "NounPlural",
  "nounDef",
  "NounDef",
  "nounPluralDef",
  "NounPluralDef",
] as const;

/**
 * The mid-sentence form of a label, derived rather than catalogued.
 *
 * Both locales lowercase a common noun mid-sentence, and every preset is a
 * common noun, so the transform is the first character and nothing else:
 * "Agent" -> "agent", "Asszisztens" -> "asszisztens", "Chatbot" -> "chatbot".
 * Deriving it keeps 20 near-duplicate catalog entries out of the translator's
 * way - entries whose only job would be to differ from their twin by one
 * letter, which is exactly the kind of pair that drifts.
 */
function lowerFirst(value: string): string {
  return value ? value.charAt(0).toLocaleLowerCase() + value.slice(1) : value;
}

/**
 * The sentence-initial casing of an article-bearing form: "az asszisztens" ->
 * "Az asszisztens".
 *
 * THIS IS NOT A DERIVED FORM, and the distinction is the whole of D-371. A
 * FORM is a lexical choice - which plural suffix vowel harmony selects, which
 * article the following sound takes - and every one of those is typed. Casing
 * the first letter is a transform over an already-chosen word, it changes no
 * lexical decision, and the catalogued presets have always taken it: the same
 * `lowerFirst` above turns the typed "Agent" into "agent" for all five of
 * them. Asking an operator for sixteen fields instead of eight to spell the
 * same word with a capital would be ceremony, not correctness.
 */
function upperFirst(value: string): string {
  return value ? value.charAt(0).toLocaleUpperCase() + value.slice(1) : value;
}

/**
 * The typed vocabulary of ONE locale, turned into the eight rendering forms.
 * The four typed values go in VERBATIM - `Noun` is the singular exactly as
 * typed, `nounDef` the definite singular exactly as typed - and only the
 * casing twins are computed, per `upperFirst` above.
 */
function formsFromTypedWords(words: AgentNounTypedWords): AgentNounForms {
  return {
    Noun: words.singular,
    noun: lowerFirst(words.singular),
    NounPlural: words.plural,
    nounPlural: lowerFirst(words.plural),
    nounDef: words.definite_singular,
    NounDef: upperFirst(words.definite_singular),
    nounPluralDef: words.definite_plural,
    NounPluralDef: upperFirst(words.definite_plural),
  };
}

/**
 * Which typed locale a render should use. Lingui locales are "hu" and "en"
 * here; anything unrecognised reads as English, which is the payload's own
 * second language and never a blank.
 */
function typedLocaleOf(locale: string): keyof AgentNounCustomWords {
  return locale.toLowerCase().startsWith("hu") ? "hu" : "en";
}

/**
 * Narrow an arbitrary payload value to a preset; anything else is the default.
 * The match is exact, mirroring the Go ValidAgentNounPreset: a near miss such
 * as "assistant" or "Assistants" is NOT a preset, because a coerced value would
 * rename the product for every tenant without anyone having chosen it.
 */
export function toAgentNounPreset(value: unknown): AgentNounPreset {
  return AGENT_NOUN_PRESETS.includes(value as AgentNounPreset)
    ? (value as AgentNounPreset)
    : DEFAULT_AGENT_NOUN_PRESET;
}

export interface AgentNounState extends AgentNounForms {
  /**
   * The resolved preset; DEFAULT_AGENT_NOUN_PRESET until the fetch lands.
   * It is "custom" only when the eight typed forms actually arrived complete -
   * an incomplete vocabulary resolves to "agents", so a consumer reading this
   * field can never find a preset whose words are missing (D-371).
   */
  preset: AgentNounPreset;
  /** True once the gateway answered. */
  isResolved: boolean;
  /** True when the fetch failed; the forms are the default wording. */
  isError: boolean;
}

/**
 * The effective platform noun, resolved into the six rendering forms.
 *
 * Unlike `useLoginIdentifierMode`, a consumer does NOT need to gate anything on
 * `isResolved`: the worst case is that a screen briefly reads "Agents" before
 * the platform's chosen word arrives, which is the product's own wording and
 * not a wrong one. The flags are exposed for surfaces that would rather wait.
 *
 * `t` here is the message-descriptor resolver from `useLingui()`; it renders a
 * descriptor in the ACTIVE locale, so the same call serves Hungarian and
 * English from the one catalog.
 */
export function useAgentNoun(): AgentNounState {
  const { i18n } = useLingui();
  const { data, isSuccess, isError } = useQuery({
    ...platformFeaturesQueryOptions(),
    select: (features) => ({
      preset: toAgentNounPreset(features.agent_noun_preset),
      words: toAgentNounCustomWords(features.agent_noun_custom),
    }),
  });

  const requested = data?.preset ?? DEFAULT_AGENT_NOUN_PRESET;
  const words = data?.words ?? null;

  // ONE resolution, and it is the same shape the Go EffectiveAgentNounPreset
  // uses: "custom" survives only if its eight words are all there. Anything
  // else - an old row, a hand-edited store, a payload that lost the
  // sub-object - lands on the catalogued default, never on a blank heading.
  if (requested === "custom" && isAgentNounCustomComplete(words) && words) {
    return {
      preset: "custom",
      isResolved: isSuccess,
      isError,
      ...formsFromTypedWords(words[typedLocaleOf(i18n.locale)]),
    };
  }

  const preset: AgentNounCatalogPreset =
    requested === "custom" ? DEFAULT_AGENT_NOUN_PRESET : requested;

  return {
    preset,
    isResolved: isSuccess,
    isError,
    ...catalogForms(i18n, preset),
  };
}

/**
 * The six catalogued forms of one preset (plus their two lowercase twins),
 * rendered in the ACTIVE locale. Shared by the resolver above and by the
 * fixed wording below, so both read the one descriptor table.
 */
function catalogForms(i18n: I18n, preset: AgentNounCatalogPreset): AgentNounForms {
  const descriptors = NOUN_DESCRIPTORS[preset];
  return {
    Noun: i18n._(descriptors.Noun),
    noun: lowerFirst(i18n._(descriptors.Noun)),
    NounPlural: i18n._(descriptors.NounPlural),
    nounPlural: lowerFirst(i18n._(descriptors.NounPlural)),
    nounDef: i18n._(descriptors.nounDef),
    NounDef: i18n._(descriptors.NounDef),
    nounPluralDef: i18n._(descriptors.nounPluralDef),
    NounPluralDef: i18n._(descriptors.NounPluralDef),
  };
}

/**
 * SPA SCOPING for components that more than one SPA renders
 * (SPEC-FRONTEND-RULE-096 (5)).
 *
 * The rule's scope is the TENANT SPA; the platform admin and workspace SPAs
 * keep the fixed wording. Only a file that no other SPA renders may call
 * `useAgentNoun()` directly, and its folder does not say which files those
 * are: ChatSettingsBlocks.tsx lives under pages/tenant, yet the workspace SPA
 * renders it in its playground (workspace AgentPage -> plugin registry ->
 * rag-chatbot ChatPlayground -> ChatSettingsTab). Which SPAs reach a file is a
 * fact of the import graph, not of where the file sits. A component that more
 * than one SPA mounts - ReportHistory in the workspace SPA's report-history
 * slot, ContextFilePicker / LockedPresetSummary in the workspace SPA,
 * ChatSettingsBlocks in the workspace playground - calls
 * `useScopedAgentNoun()`, which follows the platform word only below
 * `TenantAgentNounProvider` and gives the fixed wording everywhere else.
 * tools/check-agent-noun-inventory.py enforces this: a non-test file in the
 * import closure of apps/admin/main.tsx or apps/workspace/main.tsx that calls
 * `useAgentNoun()` directly fails the gate.
 *
 * The provider is mounted once, at the tenant SPA root (apps/tenant/App.tsx),
 * and it resolves through `useAgentNoun()` - the ONE resolver of clause (1),
 * on the one `['platform','features']` fetcher - so there is still exactly one
 * place that turns the setting into words. The scoped hook itself opens no
 * query, so a shared component needs no QueryClientProvider to render.
 */
const TenantAgentNounContext = createContext<AgentNounState | null>(null);

/** Mount ONCE, at the tenant SPA root, inside the QueryClientProvider. */
export function TenantAgentNounProvider({ children }: { children?: ReactNode }): ReactElement {
  const resolved = useAgentNoun();
  const { preset, isResolved, isError, Noun, noun, NounPlural, nounPlural } = resolved;
  const { nounDef, NounDef, nounPluralDef, NounPluralDef } = resolved;
  // A stable value, so a re-render of the root does not re-render every
  // consumer when no word changed.
  const value = useMemo<AgentNounState>(
    () => ({ preset, isResolved, isError, Noun, noun, NounPlural, nounPlural, nounDef, NounDef, nounPluralDef, NounPluralDef }),
    [preset, isResolved, isError, Noun, noun, NounPlural, nounPlural, nounDef, NounDef, nounPluralDef, NounPluralDef],
  );
  return createElement(TenantAgentNounContext.Provider, { value }, children);
}

/**
 * The agent noun for a component that more than one SPA renders: the platform
 * word inside the tenant SPA, the fixed wording (the `agents` preset, today's
 * product) in the platform admin and workspace SPAs.
 */
export function useScopedAgentNoun(): AgentNounState {
  const { i18n } = useLingui();
  const tenant = useContext(TenantAgentNounContext);
  if (tenant) return tenant;
  return fixedAgentNoun(i18n);
}

/**
 * The fixed wording RULE-096 (5) keeps outside the tenant SPA: the default
 * preset's forms in the active locale, never the platform setting.
 */
export function fixedAgentNoun(i18n: I18n): AgentNounState {
  return {
    preset: DEFAULT_AGENT_NOUN_PRESET,
    isResolved: true,
    isError: false,
    ...catalogForms(i18n, DEFAULT_AGENT_NOUN_PRESET),
  };
}

/**
 * The descriptor table, for the two consumers that cannot use the hook: the
 * unit tests, and the inventory gate's form-coverage check.
 */
export function agentNounDescriptors(): typeof NOUN_DESCRIPTORS {
  return NOUN_DESCRIPTORS;
}

/**
 * The label the platform admin's preset picker shows for one option
 * (SPEC-FRONTEND-UI-050). Rendered in the ADMIN's locale, not the tenant's -
 * the admin is choosing a word, not reading it in place.
 *
 * This function also carries the `t` literal that keeps this file visible to
 * `check-i18n-freshness.py`, whose RULE-A prefilter skips any file containing
 * no `t` template at all (tools/check-i18n-freshness.py:199-200). Without it
 * the thirty descriptors above could go missing from the catalog with the gate
 * still green.
 */
export function agentNounPresetOptionLabel(preset: AgentNounPreset): string {
  switch (preset) {
    case "assistants":
      return t`Assistants`;
    case "chatbots":
      return t`Chatbots`;
    case "chat_robots":
      return t`Chat robots`;
    case "chat_surfaces":
      return t`Chat surfaces`;
    case "custom":
      return t`Custom word`;
    case "agents":
    default:
      return t`Agents`;
  }
}
